// Global Instructions Rule Applied!
//
// Deal States strip (deal follower split, owner 2026-08-25): the deal-level
// twin of CompanyStatesStrip, bound to ONE follower's catalogue via
// `dimension` — the deal Dashboard renders the origination strip
// (team sentiment / diligence / decision urgency), the Execution tab the
// execution strip (closing progress / closing items / closing urgency).
// The server filters by agent_type_key, so execution content never reaches
// the Dashboard and vice versa.
import React, { useCallback } from 'react'
import { store } from '../../../state_manager/store.ts'
import { getAccessToken } from '../../../core/auth/authService'
import {
  createTaskFromDealState,
  fetchDealStates,
  overrideDealState
} from '../services/internalAgentsService.ts'
import { DealState } from '../../../state_manager/data_models/internal_agents_model.ts'
import { StatesStrip } from './StatesStrip.tsx'
import { StateItem } from './StateCard.tsx'

const DIMENSION_TYPE_KEYS = {
  origination: 'deal_origination_follower',
  execution: 'deal_execution_follower'
} as const

interface DealStatesStripProps {
  dealId: string
  /** Which follower's strip this is — resolves to the agent_type_key the
   *  backend filters view__deal_states by. */
  dimension: keyof typeof DIMENSION_TYPE_KEYS
  title?: string
  /** Pre-selects assignees in the create-task form (the deal form passes its
   *  deal lead). Never auto-assigns. */
  defaultAssignees?: Array<{ label: string; value: string }>
  /** Fired after a task is created so the host can refresh its Tasks panel. */
  onTaskCreated?: () => void
  /** Hands `load` to the deal form so its Refresh button reloads this panel. */
  onRefreshReady?: ((refreshFn: () => Promise<void>) => void) | null
}

const toStateItem = (s: DealState): StateItem => ({
  id: s.deal_states__id,
  stateKey: s.deal_state_types__state_key,
  label: s.deal_state_types__label,
  kind: s.deal_state_types__kind,
  conditionKey: s.deal_states__condition_key,
  conditionLabel: s.condition_label,
  conditionRank: s.condition_rank,
  summary: s.deal_states__summary,
  status: s.deal_states__status,
  startedAt: s.deal_states__started_at,
  lastConfirmedAt: s.deal_states__last_confirmed_at,
  lastEvidenceAt: s.deal_states__last_evidence_at ?? null,
  lastCheckedAt: s.deal_states__last_checked_at ?? null,
  expectedEndAt: s.deal_states__expected_end_at ?? null,
  expectedEndBasis: s.deal_states__expected_end_basis ?? null,
  userSet: s.deal_states__user_set,
  conditions: s.conditions ?? [],
  taskId: s.deal_states__task_id ?? null,
  taskSubject: s.tasks__subject ?? null,
  taskCompleted: s.tasks__completed ?? null,
  taskDueBy: s.tasks__due_by ?? null
})

export const DealStatesStrip: React.FC<DealStatesStripProps> = ({
  dealId,
  dimension,
  title = 'Current State',
  defaultAssignees = [],
  onTaskCreated,
  onRefreshReady = null
}) => {
  const token = () => getAccessToken(store.getState().auth)
  const agentTypeKey = DIMENSION_TYPE_KEYS[dimension]

  const fetchStates = useCallback(
    async () => (await fetchDealStates(dealId, agentTypeKey, token())).map(toStateItem),
    [dealId, agentTypeKey] // eslint-disable-line react-hooks/exhaustive-deps
  )

  const overrideState = useCallback(
    async (args: {
      stateKey: string
      action: 'set_condition' | 'edit_summary' | 'close'
      stateId?: string | null
      conditionKey?: string | null
      summary?: string | null
    }) =>
      overrideDealState(
        {
          deal_id: dealId,
          state_key: args.stateKey,
          action: args.action,
          state_id: args.stateId,
          condition_key: args.conditionKey,
          summary: args.summary
        },
        token()
      ),
    [dealId] // eslint-disable-line react-hooks/exhaustive-deps
  )

  const createTask = useCallback(
    async (stateId: string, taskRecord: unknown) =>
      createTaskFromDealState(stateId, taskRecord, token()),
    [] // eslint-disable-line react-hooks/exhaustive-deps
  )

  return (
    <StatesStrip
      title={title}
      fetchStates={fetchStates}
      overrideState={overrideState}
      createTaskFromState={createTask}
      defaultAssignees={defaultAssignees}
      onTaskCreated={onTaskCreated}
      subjectNoun='deal'
      collapseKey={dimension === 'execution' ? 'deal_execution_tab' : 'deal_dashboard'}
      onRefreshReady={onRefreshReady}
    />
  )
}
